import React, { createContext, useContext, useState, useCallback } from "react";
import ConfirmModal from "../components/common/ConfirmModal";

/**
 * ModalContext + useModal()
 *
 * Provides a global confirm dialog that any component can trigger:
 *
 *   const { confirm } = useModal();
 *
 *   const handleDelete = async (id) => {
 *     const yes = await confirm({
 *       variant: 'danger',
 *       title: 'Delete Teacher?',
 *       message: 'This action cannot be undone.',
 *       confirmLabel: 'Delete',
 *     });
 *     if (!yes) return;
 *     // … proceed
 *   };
 *
 * The API mirrors what you'd do with `window.confirm()` except it returns a
 * Promise<boolean> so you can `await` it, and the UX is a premium animated modal.
 */

const ModalContext = createContext(null);

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be used within <ModalProvider>");
  return ctx;
};

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    open: false,
    variant: "info",
    title: "Are you sure?",
    message: "",
    confirmLabel: "Confirm",
    secondaryLabel: "",
    cancelLabel: "Cancel",
    showInput: false,
    inputPlaceholder: "",
    inputValue: "",
    icon: null,
    resolve: null,
  });

  const confirm = useCallback(
    ({
      variant = "info",
      title = "Are you sure?",
      message = "",
      confirmLabel = "Confirm",
      cancelLabel = "Cancel",
      icon = null,
    } = {}) =>
      new Promise((resolve) => {
        setModalState({
          open: true,
          variant,
          title,
          message,
          confirmLabel,
          cancelLabel,
          showInput: false,
          inputValue: "",
          icon,
          resolve,
        });
      }),
    [],
  );

  const prompt = useCallback(
    ({
      variant = "info",
      title = "Input details",
      message = "",
      confirmLabel = "Submit",
      cancelLabel = "Cancel",
      inputPlaceholder = "Type here...",
      icon = null,
    } = {}) =>
      new Promise((resolve) => {
        setModalState({
          open: true,
          variant,
          title,
          message,
          confirmLabel,
          cancelLabel,
          showInput: true,
          inputPlaceholder,
          inputValue: "",
          icon,
          resolve,
        });
      }),
    [],
  );
  
  const choice = useCallback(
    ({
      variant = "info",
      title = "What would you like to do?",
      message = "",
      confirmLabel = "Option 1",
      secondaryLabel = "Option 2",
      cancelLabel = "Cancel",
      icon = null,
    } = {}) =>
      new Promise((resolve) => {
        setModalState({
          open: true,
          variant,
          title,
          message,
          confirmLabel,
          secondaryLabel,
          cancelLabel,
          showInput: false,
          inputValue: "",
          icon,
          resolve,
        });
      }),
    [],
  );

  const handleConfirm = () => {
    if (modalState.showInput) {
      modalState.resolve?.(modalState.inputValue);
    } else {
      modalState.resolve?.(true);
    }
    setModalState((s) => ({ ...s, open: false }));
  };

  const handleCancel = () => {
    modalState.resolve?.(null);
    setModalState((s) => ({ ...s, open: false }));
  };

  const handleSecondary = () => {
    modalState.resolve?.("secondary");
    setModalState((s) => ({ ...s, open: false }));
  };

  return (
    <ModalContext.Provider value={{ confirm, prompt, choice }}>
      {children}
      <ConfirmModal
        open={modalState.open}
        variant={modalState.variant}
        title={modalState.title}
        message={modalState.message}
        confirmLabel={modalState.confirmLabel}
        secondaryLabel={modalState.secondaryLabel}
        cancelLabel={modalState.cancelLabel}
        showInput={modalState.showInput}
        inputPlaceholder={modalState.inputPlaceholder}
        inputValue={modalState.inputValue}
        setInputValue={(val) => setModalState((s) => ({ ...s, inputValue: val }))}
        icon={modalState.icon}
        onConfirm={handleConfirm}
        onSecondary={handleSecondary}
        onCancel={handleCancel}
      />
    </ModalContext.Provider>
  );
};

export default ModalContext;
